import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { MapPin, Calendar, Globe, IndianRupee, CheckCircle, XCircle, Hotel, ListOrdered, Building2, FileText, UtensilsCrossed, ArrowLeft, Package, Download, Ticket, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Lexend font loader for jsPDF (cached on window) ----
const LEXEND_URLS = {
  normal: "https://cdn.jsdelivr.net/gh/googlefonts/lexend@main/fonts/lexend/ttf/Lexend-Regular.ttf",
  bold: "https://cdn.jsdelivr.net/gh/googlefonts/lexend@main/fonts/lexend/ttf/Lexend-Bold.ttf",
};
const arrayBufferToBase64 = (buf) => {
  let binary = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};
const loadLexendForJsPDF = async (doc) => {
  try {
    if (!window.__lexendB64) {
      const [normalRes, boldRes] = await Promise.all([
        fetch(LEXEND_URLS.normal),
        fetch(LEXEND_URLS.bold),
      ]);
      if (!normalRes.ok || !boldRes.ok) throw new Error("Font fetch failed");
      const [normalBuf, boldBuf] = await Promise.all([normalRes.arrayBuffer(), boldRes.arrayBuffer()]);
      window.__lexendB64 = {
        normal: arrayBufferToBase64(normalBuf),
        bold: arrayBufferToBase64(boldBuf),
      };
    }
    doc.addFileToVFS("Lexend-Regular.ttf", window.__lexendB64.normal);
    doc.addFont("Lexend-Regular.ttf", "Lexend", "normal");
    doc.addFileToVFS("Lexend-Bold.ttf", window.__lexendB64.bold);
    doc.addFont("Lexend-Bold.ttf", "Lexend", "bold");
    return true;
  } catch (e) {
    console.warn("Lexend load failed, falling back to helvetica:", e);
    return false;
  }
};

const getStartingPrice = (pkg) => {
  if (!pkg) return 0;
  if (pkg.hotel_options && pkg.hotel_options.length > 0) {
    const prices = [];
    pkg.hotel_options.forEach(opt => {
      (opt.pax_rows || []).forEach(r => {
        const cpai = r.prices && r.prices.CPAI;
        if (cpai != null && cpai > 0) prices.push(Number(cpai));
      });
    });
    if (prices.length > 0) return Math.min(...prices);
  }
  return pkg.per_adult_price || pkg.price || 0;
};

const AgentPackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfDialog, setPdfDialog] = useState(false);
  const [pdfForm, setPdfForm] = useState({
    hotel_option_idxs: [0],
    meal_plans: ["CPAI"],
    adults: 2,
    cwb: 0,
    cnb: 0,
    markup_type: "per_pax",
    markup_amount: 0,
    gst_applicable: true,
    guest_name: "",
    guest_mobile: "",
    travel_date: "",
  });
  const [generating, setGenerating] = useState(false);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    hotel_option_idx: 0,
    meal_plan: "CPAI",
    adults: 2,
    cwb: 0,
    cnb: 0,
    gst_applicable: true,
    guest_name: "",
    guest_mobile: "",
    travel_date: "",
    requirements: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/packages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setPkg(res.data);
      } catch {
        toast.error("Failed to load package");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token]);

  const bookingOpt = () => {
    if (!pkg || !pkg.hotel_options || pkg.hotel_options.length === 0) return null;
    return pkg.hotel_options[bookingForm.hotel_option_idx] || pkg.hotel_options[0];
  };
  const calcBookingTotals = () => {
    const opt = bookingOpt();
    const adults = parseInt(bookingForm.adults) || 0;
    const cwb = parseInt(bookingForm.cwb) || 0;
    const cnb = parseInt(bookingForm.cnb) || 0;
    let perAdult = 0, cwbRate = 0, cnbRate = 0, vehicle = "";
    if (opt) {
      const sorted = [...(opt.pax_rows || [])].sort((a, b) => b.pax - a.pax);
      const row = sorted.find(r => r.pax === adults) || sorted.find(r => r.pax <= adults) || (opt.pax_rows || [])[(opt.pax_rows || []).length - 1];
      perAdult = row?.prices?.[bookingForm.meal_plan] || row?.prices?.CPAI || 0;
      cwbRate = opt.cwb_price || 0;
      cnbRate = opt.cnb_price || 0;
      vehicle = row?.vehicle || "";
    } else if (pkg) {
      perAdult = pkg.per_adult_price || pkg.price || 0;
      cwbRate = pkg.per_cwb_price || 0;
      cnbRate = pkg.per_cnb_price || 0;
    }
    const subtotal = (perAdult * adults) + (cwbRate * cwb) + (cnbRate * cnb);
    const gst = bookingForm.gst_applicable ? Math.round(subtotal * 0.05) : 0;
    const final = subtotal + gst;
    return { perAdult, cwbRate, cnbRate, adults, cwb, cnb, subtotal, gst, final, vehicle };
  };
  const submitBooking = async () => {
    if (!pkg) return;
    if (!bookingForm.travel_date) { toast.error("Please select a travel date"); return; }
    if (!bookingForm.guest_name.trim()) { toast.error("Please enter guest name"); return; }
    const adults = parseInt(bookingForm.adults) || 0;
    if (adults < 1) { toast.error("At least 1 adult required"); return; }
    const cwb = parseInt(bookingForm.cwb) || 0;
    const cnb = parseInt(bookingForm.cnb) || 0;
    const nights = pkg.duration_nights || parseInt(pkg.duration) || 1;
    const tDate = new Date(bookingForm.travel_date);
    const rDate = new Date(tDate.getTime() + nights * 86400000);
    const iso = (d) => d.toISOString().slice(0, 10);
    const opt = bookingOpt();
    const firstStay = (opt?.stays && opt.stays[0]) || opt || {};
    const hotelCat = pkg.category === 'luxury' ? '5-star' : pkg.category === 'premium' ? '4-star' : '3-star';
    const payload = {
      trip_type: pkg.trip_type || 'domestic',
      destination: pkg.destination,
      travel_date: iso(tDate),
      return_date: iso(rDate),
      pax_adults: adults,
      pax_children: cwb + cnb,
      pax_infants: 0,
      no_of_rooms: Math.max(1, Math.ceil((adults + cwb) / 2)),
      cwb,
      cnb,
      hotel_category: hotelCat,
      hotel_options_count: pkg.hotel_options?.length || 1,
      meal_plan: bookingForm.meal_plan,
      client_name: bookingForm.guest_name.trim(),
      client_phone: bookingForm.guest_mobile.trim(),
      client_email: "",
      requirements: `PACKAGE BOOKING REQUEST: ${pkg.title}\nSelected Option ${bookingForm.hotel_option_idx + 1}${firstStay.hotel_name ? ` (${firstStay.hotel_name})` : ''}\nMeal Plan: ${bookingForm.meal_plan}\nGST: ${bookingForm.gst_applicable ? '5% applicable' : 'Not applicable'}${bookingForm.requirements ? '\nNotes: ' + bookingForm.requirements : ''}`,
    };
    setBookingSubmitting(true);
    try {
      const res = await axios.post(`${API}/quotes`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Booking request sent! Quote #${res.data.quote_number || res.data.id?.slice(0, 8)}`);
      setBookingDialog(false);
      setTimeout(() => navigate('/agent/quotes'), 900);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to submit booking");
    } finally {
      setBookingSubmitting(false);
    }
  };

  // ----- PDF Quote calculation -----
  const getPaxRow = (opt, paxCount) => {
    if (!opt || !opt.pax_rows) return null;
    const sorted = [...opt.pax_rows].sort((a, b) => b.pax - a.pax);
    return sorted.find(r => r.pax === paxCount) || sorted.find(r => r.pax <= paxCount) || opt.pax_rows[opt.pax_rows.length - 1];
  };

  // Compute totals for a single (hotel option index, meal plan) combo.
  // Markup is spread across all selected combos so the agent's total collected
  // across PDF combos still equals (sum of subtotals + markup).
  const calcComboTotals = (optIdx, mealPlan, combosCount = 1) => {
    const opt = (pkg?.hotel_options && pkg.hotel_options[optIdx]) || null;
    const adults = parseInt(pdfForm.adults) || 0;
    const cwb = parseInt(pdfForm.cwb) || 0;
    const cnb = parseInt(pdfForm.cnb) || 0;
    const markupAmt = parseFloat(pdfForm.markup_amount) || 0;
    let perAdult = 0, cwbRate = 0, cnbRate = 0, vehicle = "", row = null;
    if (opt) {
      row = getPaxRow(opt, adults);
      perAdult = row?.prices?.[mealPlan] || row?.prices?.CPAI || 0;
      cwbRate = opt.cwb_price || 0;
      cnbRate = opt.cnb_price || 0;
      vehicle = row?.vehicle || "";
    } else if (pkg) {
      perAdult = pkg.per_adult_price || pkg.price || 0;
      cwbRate = pkg.per_cwb_price || 0;
      cnbRate = pkg.per_cnb_price || 0;
    }
    const subtotal = (perAdult * adults) + (cwbRate * cwb) + (cnbRate * cnb);
    const totalPax = adults + cwb + cnb;
    // Apply full markup per combo (each combo shows its own marked-up total)
    const markup = pdfForm.markup_type === "per_pax" ? markupAmt * totalPax : markupAmt;
    const grand = subtotal + markup;
    const gst = pdfForm.gst_applicable ? Math.round(grand * 0.05) : 0;
    const final = grand + gst;
    return { perAdult, cwbRate, cnbRate, adults, cwb, cnb, subtotal, markup, grand, gst, final, vehicle, row, opt, optIdx, mealPlan };
  };

  // Aggregated meal plans available across currently selected hotel options (union)
  const getAvailableMealPlans = () => {
    const idxs = pdfForm.hotel_option_idxs.length > 0 ? pdfForm.hotel_option_idxs : [0];
    const plans = new Set();
    idxs.forEach(i => {
      const o = pkg?.hotel_options?.[i];
      (o?.meal_plans && o.meal_plans.length > 0 ? o.meal_plans : ["CPAI"]).forEach(p => plans.add(p));
    });
    return Array.from(plans);
  };

  // All selected combos (option × meal_plan) — preserves agent-selected order
  const getSelectedCombos = () => {
    const idxs = pdfForm.hotel_option_idxs.length > 0 ? pdfForm.hotel_option_idxs : [0];
    const mps = pdfForm.meal_plans.length > 0 ? pdfForm.meal_plans : ["CPAI"];
    const combos = [];
    idxs.forEach(i => {
      const o = pkg?.hotel_options?.[i];
      const optPlans = (o?.meal_plans && o.meal_plans.length > 0) ? o.meal_plans : ["CPAI"];
      mps.forEach(mp => {
        // only include the (option, meal) if the option actually has that meal plan
        if (optPlans.includes(mp)) combos.push({ optIdx: i, mealPlan: mp });
      });
    });
    // If filtering produced nothing, fall back to first pair
    if (combos.length === 0) combos.push({ optIdx: idxs[0], mealPlan: mps[0] });
    return combos;
  };

  // Backwards compatible: first combo's totals for preview default
  const calcTotals = () => {
    const combos = getSelectedCombos();
    return calcComboTotals(combos[0].optIdx, combos[0].mealPlan, combos.length);
  };

  const fmt = (n) => `INR ${Math.round(n).toLocaleString('en-IN')}`;

  const generatePDF = async () => {
    if (!pkg) return;
    const combos = getSelectedCombos();
    const selectedOptIdxs = [...new Set(combos.map(c => c.optIdx))];
    const totals = calcTotals(); // first-combo totals (used for Trip Overview pax + itinerary vehicle)
    setGenerating(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const lexendOk = await loadLexendForJsPDF(doc);
      const FONT = lexendOk ? 'Lexend' : 'helvetica';
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const mx = 12;
      const cw = pageW - 2 * mx;
      let y = 12;
      // Show OPTION N badge only when multiple hotel options are included in this PDF
      const multiOpt = selectedOptIdxs.length > 1;
      const SECTION_GAP = 12; // ~34px gap between major sections
      const setFont = (size, color = [60, 60, 60], style = 'normal') => {
        doc.setFontSize(size);
        doc.setTextColor(color[0], color[1], color[2]);
        doc.setFont(FONT, style);
      };
      const drawRect = (x, yy, w, h, color = [240, 240, 240], radius = 2) => {
        doc.setFillColor(color[0], color[1], color[2]);
        doc.roundedRect(x, yy, w, h, radius, radius, 'F');
      };
      const checkPage = (needed = 20) => {
        if (y + needed > pageH - 15) { doc.addPage(); y = 12; }
      };
      const emerald = [16, 122, 87];
      const dark = [45, 55, 72];
      const grey = [107, 114, 128];
      const purple = [124, 58, 237];
      const red = [185, 28, 28];
      const green = [21, 128, 61];

      // ---- HEADER ----
      drawRect(mx, y, 30, 20, [240, 240, 240], 3);
      if (user?.logo_url) {
        try {
          const ext = (user.logo_url.startsWith('data:image/jpeg') || user.logo_url.startsWith('data:image/jpg')) ? 'JPEG' : 'PNG';
          doc.addImage(user.logo_url, ext, mx + 1, y + 1, 28, 18, undefined, 'FAST');
        } catch { setFont(7, grey); doc.text('LOGO', mx + 10, y + 11); }
      } else { setFont(7, grey); doc.text('LOGO', mx + 10, y + 11); }
      setFont(14, dark, 'bold');
      doc.text(user?.company_name || 'Travel Company', mx + 34, y + 6);
      setFont(8, grey);
      const addrParts = [user?.address, user?.city, user?.state].filter(Boolean);
      if (addrParts.length) doc.text(addrParts.join(', '), mx + 34, y + 11);
      if (user?.phone) doc.text(user.phone, mx + 34, y + 15);
      if (user?.email) doc.text(user.email, mx + 34, y + 19);
      y += 26;

      // ---- QUOTATION DETAILS TITLE ----
      setFont(18, dark, 'bold');
      doc.text('Quotation Details', mx, y); y += 8;
      doc.setDrawColor(220); doc.line(mx, y, mx + cw, y); y += 6;

      // ---- TRIP OVERVIEW ----
      setFont(11, dark, 'bold'); doc.text('Trip Overview', mx, y); y += 6;
      const quoteNo = `#HH-PKG-${Date.now().toString().slice(-6)}`;
      const nights = pkg.duration_nights || 0;
      const tDate = pdfForm.travel_date ? new Date(pdfForm.travel_date) : null;
      const rDate = tDate ? new Date(tDate.getTime() + nights * 86400000) : null;
      const fmtDate = (d) => d ? `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}` : 'TBD';
      const travellersLabel = `${totals.adults} Adult${totals.adults !== 1 ? 's' : ''}${totals.cwb > 0 ? ` + ${totals.cwb} CWB` : ''}${totals.cnb > 0 ? ` + ${totals.cnb} CNB` : ''}`;
      const overview = [
        ['Quotation No', quoteNo, 'No of Nights', `${nights} Nights`],
        ['Destination', pkg.destination || '-', 'Travel Date', fmtDate(tDate)],
        ['Travellers', travellersLabel, 'Return Date', fmtDate(rDate)],
      ];
      setFont(9, dark);
      const colL = mx; const colLV = mx + 34; const colR = mx + cw/2 + 4; const colRV = colR + 34;
      overview.forEach(row => {
        checkPage(6);
        setFont(9, grey); doc.text(row[0], colL, y);
        setFont(9, dark, 'bold'); doc.text(String(row[1]), colLV, y);
        setFont(9, grey); doc.text(row[2], colR, y);
        setFont(9, dark, 'bold'); doc.text(String(row[3]), colRV, y);
        y += 5.5;
      });
      y += 3;

      // ---- GUEST INFORMATION ----
      setFont(11, dark, 'bold'); doc.text('Guest Information', mx, y); y += 6;
      setFont(9, grey); doc.text('Guest Name', colL, y);
      setFont(9, dark, 'bold'); doc.text(pdfForm.guest_name || 'Not filled', colLV, y);
      setFont(9, grey); doc.text('Guest Mobile', colR, y);
      setFont(9, dark, 'bold'); doc.text(pdfForm.guest_mobile || 'Not filled', colRV, y);
      y += SECTION_GAP;

      // ---- HOTEL DETAILS ----
      if (selectedOptIdxs.length > 0 && pkg.hotel_options && pkg.hotel_options.length > 0) {
        checkPage(20);
        setFont(11, dark, 'bold'); doc.text('Hotel Details', mx, y); y += 6;
        selectedOptIdxs.forEach((oIdx) => {
          const opt = pkg.hotel_options[oIdx];
          if (!opt) return;
          checkPage(30);
          // Option label (only when multiple hotel options are included in this PDF)
          if (multiOpt) {
            drawRect(mx, y, 35, 7, purple, 2);
            setFont(9, [255, 255, 255], 'bold');
            doc.text(`OPTION ${oIdx + 1}`, mx + 3, y + 4.8);
            y += 10;
          }
          const stays = opt.stays && opt.stays.length > 0 ? opt.stays : [{
            nights: opt.nights, destination: opt.destination, hotel_name: opt.hotel_name, room_category: opt.room_category
          }];
          let checkIn = tDate ? new Date(tDate) : null;
          // Meal plan label for this option (show first selected meal plan that this opt supports, or all)
          const optPlans = (opt.meal_plans && opt.meal_plans.length > 0) ? opt.meal_plans : ["CPAI"];
          const planLabel = pdfForm.meal_plans.filter(p => optPlans.includes(p)).join(' / ') || optPlans[0];
          stays.forEach(stay => {
            checkPage(24);
            const stayNights = parseInt((stay.nights || '1').toString().replace(/\D/g, '')) || 1;
            const cOut = checkIn ? new Date(checkIn.getTime() + stayNights * 86400000) : null;
            // Card
            drawRect(mx, y, cw, 22, [250, 250, 253], 2);
            doc.setDrawColor(200); doc.setLineWidth(0.2);
            doc.roundedRect(mx, y, cw, 22, 2, 2);
            // Hotel name
            setFont(11, dark, 'bold'); doc.text(stay.hotel_name || 'Hotel', mx + 4, y + 6);
            // Star rating right aligned
            if (pkg.category) {
              setFont(8, purple, 'bold');
              doc.text((pkg.category === 'luxury' ? '5 Star' : pkg.category === 'premium' ? '4 Star' : '3 Star'), mx + cw - 4, y + 6, { align: 'right' });
            }
            // Details grid
            const gridY = y + 11;
            const labelStyle = () => setFont(7, grey);
            const valStyle = () => setFont(8, dark, 'bold');
            const x1 = mx + 4, x2 = mx + 44, x3 = mx + 84, x4 = mx + 124;
            labelStyle(); doc.text('Location', x1, gridY); valStyle(); doc.text(stay.destination || '-', x1, gridY + 4);
            labelStyle(); doc.text('No of Rooms', x2, gridY); valStyle(); doc.text(String(Math.ceil((totals.adults + totals.cwb) / 2)) || '1', x2, gridY + 4);
            labelStyle(); doc.text('Room Category', x3, gridY); valStyle(); doc.text(stay.room_category || '-', x3, gridY + 4);
            labelStyle(); doc.text('Meal Plan', x4, gridY); valStyle(); doc.text(planLabel, x4, gridY + 4);
            const gridY2 = y + 20;
            labelStyle(); doc.text('Nights', x1, gridY2 - 1.5); valStyle(); doc.text(String(stayNights), x1 + 14, gridY2 - 1.5);
            labelStyle(); doc.text('Check In', x2, gridY2 - 1.5); valStyle(); doc.text(fmtDate(checkIn), x2 + 16, gridY2 - 1.5);
            labelStyle(); doc.text('Check Out', x3, gridY2 - 1.5); valStyle(); doc.text(fmtDate(cOut), x3 + 18, gridY2 - 1.5);
            y += 25;
            if (cOut) checkIn = cOut;
          });
          y += 3;
        });
        y += SECTION_GAP - 3;
      }

      // ---- DAY-WISE ITINERARY ----
      if (pkg.itinerary && pkg.itinerary.length > 0) {
        checkPage(20);
        setFont(14, dark, 'bold'); doc.text('Day-wise Itinerary', mx, y); y += 7;
        const startDate = tDate ? new Date(tDate) : null;
        pkg.itinerary.forEach((d, i) => {
          checkPage(24);
          const dayDate = startDate ? new Date(startDate.getTime() + (d.day - 1) * 86400000) : null;
          const dayTxt = dayDate ? `${dayDate.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()} - ${String(dayDate.getDate()).padStart(2,'0')}/${String(dayDate.getMonth()+1).padStart(2,'0')}/${dayDate.getFullYear()}` : '';
          const loc = (d.title || '').toUpperCase();
          // Green bar
          drawRect(mx, y, cw, 7, emerald, 2);
          setFont(10, [255, 255, 255], 'bold');
          const bar = `DAY ${d.day}${dayDate ? `   •   ${dayTxt}` : ''}${loc ? `   •   ${loc}` : ''}`;
          doc.text(bar, mx + 3, y + 5);
          y += 10;
          // Description
          if (d.description) {
            setFont(8, dark);
            const lines = doc.splitTextToSize(d.description, cw);
            checkPage(lines.length * 4 + 4);
            doc.text(lines, mx, y); y += lines.length * 3.8;
          }
          // Activities list
          const acts = Array.isArray(d.activities) ? d.activities : (typeof d.activities === 'string' ? d.activities.split(',').map(a => a.trim()).filter(Boolean) : []);
          if (acts.length > 0) {
            acts.forEach(a => {
              const label = typeof a === 'string' ? a : (a.title || a.tour_name);
              if (!label) return;
              checkPage(5);
              setFont(9, emerald, 'bold'); doc.text(`• ${label.toUpperCase()}`, mx, y); y += 4;
              if (typeof a === 'object' && a.description) {
                setFont(8, grey);
                const dl = doc.splitTextToSize(a.description, cw - 4);
                checkPage(dl.length * 4);
                doc.text(dl, mx + 3, y); y += dl.length * 3.6;
              }
            });
          }
          // Vehicle
          if (totals.vehicle) {
            checkPage(5);
            setFont(8, grey); doc.text(`Transfer: Private - ${totals.vehicle}  |  Vehicle: ${totals.vehicle}`, mx, y); y += 5;
          }
          y += 2;
        });
        y += SECTION_GAP;
      }

      // ---- INCLUSIONS / EXCLUSIONS ----
      const inc = pkg.inclusions || [];
      const exc = pkg.exclusions || [];
      if (inc.length > 0 || exc.length > 0) {
        checkPage(30);
        const halfW = (cw - 4) / 2;
        // Red header boxes
        drawRect(mx, y, halfW, 7, red, 2);
        setFont(10, [255, 255, 255], 'bold'); doc.text('Inclusions', mx + 3, y + 5);
        drawRect(mx + halfW + 4, y, halfW, 7, red, 2);
        doc.text('Exclusions', mx + halfW + 7, y + 5);
        y += 10;
        const startY = y;
        setFont(8, dark);
        let leftY = startY;
        inc.forEach(item => {
          checkPage(5);
          const lines = doc.splitTextToSize(`• ${item}`, halfW - 4);
          doc.text(lines, mx + 2, leftY);
          leftY += lines.length * 4;
        });
        let rightY = startY;
        exc.forEach(item => {
          const lines = doc.splitTextToSize(`• ${item}`, halfW - 4);
          doc.text(lines, mx + halfW + 6, rightY);
          rightY += lines.length * 4;
        });
        y = Math.max(leftY, rightY) + SECTION_GAP;
      }

      // ---- TERMS & CONDITIONS ----
      if (pkg.terms_conditions) {
        checkPage(20);
        setFont(11, dark, 'bold'); doc.text('Terms & Conditions', mx, y); y += 6;
        setFont(8, grey);
        const lines = doc.splitTextToSize(pkg.terms_conditions, cw);
        lines.forEach(l => { checkPage(5); doc.text(l, mx, y); y += 3.8; });
        y += SECTION_GAP;
      }

      // ---- COST BREAKDOWN (no markup shown) ----
      checkPage(24);
      setFont(11, dark, 'bold'); doc.text('Cost Breakdown', mx, y); y += 8;

      // Group combos by option, render one sub-section per option containing each selected meal plan row
      selectedOptIdxs.forEach((oIdx) => {
        const opt = pkg.hotel_options?.[oIdx];
        const optCombos = combos.filter(c => c.optIdx === oIdx);
        if (optCombos.length === 0) return;
        checkPage(20 + optCombos.length * 22);
        // OPTION N purple badge + hotel name header (only when multiple options)
        if (multiOpt) {
          drawRect(mx, y, 35, 7, purple, 2);
          setFont(9, [255, 255, 255], 'bold');
          doc.text(`OPTION ${oIdx + 1}`, mx + 3, y + 4.8);
          const firstStay = (opt?.stays && opt.stays[0]) || opt;
          if (firstStay?.hotel_name) {
            setFont(10, dark, 'bold'); doc.text(firstStay.hotel_name, mx + 40, y + 4.8);
          }
          y += 10;
        }
        optCombos.forEach((cmb) => {
          const t = calcComboTotals(cmb.optIdx, cmb.mealPlan, combos.length);
          // Effective per-adult (markup folded in, so client doesn't see markup line)
          const effectivePerAdult = t.adults > 0 ? Math.round((t.final - (t.cwbRate * t.cwb) - (t.cnbRate * t.cnb)) / t.adults) : 0;
          checkPage(22);
          // Meal plan chip
          setFont(9, emerald, 'bold');
          doc.text(`Meal Plan: ${cmb.mealPlan}`, mx, y); y += 5;
          setFont(10, dark, 'bold');
          doc.text(`PER ADULT COST:  ${fmt(effectivePerAdult)} x ${t.adults}`, mx, y); y += 5.5;
          if (t.cwb > 0) { doc.text(`PER CWB COST:  ${fmt(t.cwbRate)} x ${t.cwb}`, mx, y); y += 5.5; }
          if (t.cnb > 0) { doc.text(`PER CNB COST:  ${fmt(t.cnbRate)} x ${t.cnb}`, mx, y); y += 5.5; }
          setFont(12, green, 'bold');
          doc.text(`TOTAL COST:  ${fmt(t.final)}`, mx, y); y += 7;
          // Separator between combos of the same option
          doc.setDrawColor(230); doc.setLineWidth(0.2); doc.line(mx, y, mx + cw, y); y += 4;
        });
        y += 3;
      });
      y += SECTION_GAP - 3;

      // ---- TRAVEL ADVISOR ----
      checkPage(36);
      drawRect(mx, y, cw, 32, [249, 250, 251], 3);
      setFont(8, grey, 'bold'); doc.text('YOUR TRAVEL ADVISOR', mx + 4, y + 5);
      const picX = mx + 4; const picY = y + 8; const picSize = 20;
      if (user?.profile_picture_url) {
        try {
          const ext = (user.profile_picture_url.startsWith('data:image/jpeg') || user.profile_picture_url.startsWith('data:image/jpg')) ? 'JPEG' : 'PNG';
          doc.addImage(user.profile_picture_url, ext, picX, picY, picSize, picSize, undefined, 'FAST');
        } catch { drawRect(picX, picY, picSize, picSize, emerald, 10); }
      } else {
        drawRect(picX, picY, picSize, picSize, emerald, 10);
        const initials = (user?.name || 'A').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
        setFont(10, [255, 255, 255], 'bold'); doc.text(initials, picX + picSize / 2, picY + picSize / 2 + 2, { align: 'center' });
      }
      const txX = picX + picSize + 6;
      if (user?.name) { setFont(11, dark, 'bold'); doc.text(user.name, txX, y + 14); }
      if (user?.designation) { setFont(8, grey); doc.text(user.designation, txX, y + 19); }
      if (user?.phone) { setFont(8, dark); doc.text(`Mobile: ${user.phone}`, txX, y + 24); }
      if (user?.email) { setFont(8, grey); doc.text(user.email, txX, y + 28); }
      y += 36;

      // ---- FOOTER ----
      const genDate = (() => { const d = new Date(); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })();
      setFont(7, grey);
      doc.text(`Generated on ${genDate}  |  ${user?.company_name || 'Travel Company'}`, pageW / 2, pageH - 8, { align: 'center' });

      // Save
      const optLabel = [...selectedOptIdxs].sort((a, b) => a - b).map(i => i + 1).join('_');
      const filename = `${pkg.title.replace(/[^a-z0-9]+/gi, '_')}_Quote_Option${optLabel}.pdf`;
      doc.save(filename);
      toast.success('PDF downloaded');
      setPdfDialog(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout type="agent">
        <div className="space-y-4">
          <div className="h-12 bg-gray-100 rounded animate-pulse" />
          <div className="h-64 bg-gray-100 rounded animate-pulse" />
          <div className="h-40 bg-gray-100 rounded animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  if (!pkg) {
    return (
      <DashboardLayout type="agent">
        <Card className="bg-white border-gray-200"><CardContent className="py-16 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-lg font-medium">Package not found</p>
          <Button onClick={() => navigate('/agent/packages')} className="mt-4 bg-emerald-600 hover:bg-emerald-700">Back to Packages</Button>
        </CardContent></Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="agent">
      <div className="max-w-6xl mx-auto space-y-6 pb-8" data-testid="agent-package-detail-page">
        {/* Back + Title bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/agent/packages')} className="text-gray-600" data-testid="pkg-detail-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">{pkg.title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setPdfDialog(true)} variant="outline" size="lg" className="gap-2 border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-xl px-6 font-semibold shadow-sm" data-testid="pkg-detail-download-pdf">
              <Download className="w-5 h-5" /> Download PDF
            </Button>
            <Button onClick={() => setBookingDialog(true)} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 rounded-xl px-6 font-semibold shadow-sm" data-testid="pkg-detail-book-now">
              <Ticket className="w-5 h-5" /> Book Now
            </Button>
          </div>
        </div>

        {/* Hero image */}
        {pkg.image_url && (
          <div className="h-64 md:h-80 rounded-2xl overflow-hidden shadow-sm">
            <img src={pkg.image_url} alt={pkg.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Info cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-emerald-50 rounded-lg p-4 text-center border border-emerald-100">
            <MapPin className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
            <p className="text-xs text-gray-500">Destination</p>
            <p className="text-base font-bold text-gray-800">{pkg.destination}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-100">
            <Calendar className="w-4 h-4 text-blue-600 mx-auto mb-1" />
            <p className="text-xs text-gray-500">Duration</p>
            <p className="text-base font-bold text-gray-800">{pkg.duration_nights || parseInt(pkg.duration) || 0}N / {pkg.duration_days || 0}D</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-100">
            <Globe className="w-4 h-4 text-purple-600 mx-auto mb-1" />
            <p className="text-xs text-gray-500">Type</p>
            <p className="text-base font-bold text-gray-800 capitalize">{pkg.trip_type}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 text-center border border-amber-100">
            <IndianRupee className="w-4 h-4 text-amber-600 mx-auto mb-1" />
            <p className="text-xs text-gray-500">Starting from</p>
            <p className="text-base font-bold text-emerald-700">
              {getStartingPrice(pkg) > 0 ? `INR ${getStartingPrice(pkg).toLocaleString()}` : 'On Request'}
            </p>
          </div>
        </div>

        {/* Description */}
        {pkg.description && (
          <Card className="bg-white"><CardContent className="pt-6">
            <h4 className="text-base font-semibold text-gray-700 mb-2">Description</h4>
            <p className="text-sm text-gray-600 leading-relaxed">{pkg.description}</p>
          </CardContent></Card>
        )}

        {/* Highlights */}
        {pkg.highlights?.length > 0 && (
          <Card className="bg-white"><CardContent className="pt-6">
            <h4 className="text-base font-semibold text-gray-700 mb-3">Highlights</h4>
            <div className="flex flex-wrap gap-2">
              {pkg.highlights.map((h, i) => <Badge key={i} className="bg-emerald-50 text-emerald-700 border-emerald-100 text-sm py-1 px-3">{h}</Badge>)}
            </div>
          </CardContent></Card>
        )}

        {/* Hotel Options */}
        {pkg.hotel_options && pkg.hotel_options.length > 0 && (
          <Card className="bg-white"><CardContent className="pt-6">
            <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-1.5">
              <Building2 className="w-5 h-5 text-emerald-600" /> Hotel Options ({pkg.hotel_options.length})
            </h4>
            <div className="space-y-4" data-testid="pkg-hotel-options">
              {pkg.hotel_options.map((opt, oIdx) => {
                const stays = opt.stays && opt.stays.length > 0 ? opt.stays : [{
                  nights: opt.nights, destination: opt.destination, hotel_name: opt.hotel_name, room_category: opt.room_category
                }].filter(s => s.nights || s.destination || s.hotel_name);
                const mealPlans = opt.meal_plans && opt.meal_plans.length > 0 ? opt.meal_plans : ["CPAI"];
                return (
                  <div key={opt.id || oIdx} className="border-2 border-emerald-200 rounded-lg overflow-hidden bg-emerald-50/30" data-testid={`hotel-option-${oIdx}`}>
                    <div className="bg-emerald-600 text-white px-4 py-2.5 flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-white text-emerald-700 text-sm font-bold flex items-center justify-center">{oIdx + 1}</span>
                      <span className="text-base font-bold">Hotel Option {oIdx + 1}</span>
                    </div>
                    {stays.length > 0 && (
                      <div className="p-4 space-y-2 bg-white border-b border-gray-100">
                        {stays.map((stay, sIdx) => (
                          <div key={sIdx} className="flex items-center gap-3 text-sm flex-wrap">
                            <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-700 font-bold flex items-center justify-center flex-shrink-0 text-xs">{sIdx + 1}</span>
                            {stay.nights && <Badge className="bg-blue-100 text-blue-700 border border-blue-200">{stay.nights}</Badge>}
                            {stay.destination && <span className="font-semibold text-gray-800">{stay.destination}</span>}
                            {stay.hotel_name && <span className="text-gray-600">• {stay.hotel_name}</span>}
                            {stay.room_category && <span className="text-gray-400">• {stay.room_category}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {opt.pax_rows && opt.pax_rows.length > 0 && (
                      <div className="p-4 bg-white">
                        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><UtensilsCrossed className="w-3.5 h-3.5" /> Meal Plans: {mealPlans.join(", ")}</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="py-2 px-3 text-left font-semibold text-gray-700">PAX</th>
                                {mealPlans.map(p => <th key={p} className="py-2 px-3 text-left font-semibold text-gray-700">{p} (INR)</th>)}
                                <th className="py-2 px-3 text-left font-semibold text-gray-700">VEHICLE</th>
                              </tr>
                            </thead>
                            <tbody>
                              {opt.pax_rows.map((row, rIdx) => (
                                <tr key={rIdx} className={`border-t border-gray-100 ${row.pax === 2 ? 'bg-amber-50/40' : ''}`}>
                                  <td className="py-2 px-3 font-semibold">{row.pax} PAX</td>
                                  {mealPlans.map(p => (
                                    <td key={p} className="py-2 px-3 text-emerald-700 font-semibold">
                                      {row.prices && row.prices[p] > 0 ? Number(row.prices[p]).toLocaleString() : '-'}
                                    </td>
                                  ))}
                                  <td className="py-2 px-3 text-gray-600">{row.vehicle || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {(opt.cwb_price > 0 || opt.cnb_price > 0) && (
                          <div className="flex gap-6 mt-3 text-sm">
                            {opt.cwb_price > 0 && <span className="text-gray-600">CWB: <span className="font-bold text-emerald-700">INR {Number(opt.cwb_price).toLocaleString()}</span></span>}
                            {opt.cnb_price > 0 && <span className="text-gray-600">CNB: <span className="font-bold text-emerald-700">INR {Number(opt.cnb_price).toLocaleString()}</span></span>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent></Card>
        )}

        {/* Itinerary */}
        {pkg.itinerary?.length > 0 && (
          <Card className="bg-white"><CardContent className="pt-6">
            <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-1.5">
              <ListOrdered className="w-5 h-5 text-indigo-500" /> Day-wise Itinerary
            </h4>
            <div className="space-y-3" data-testid="pkg-itinerary">
              {pkg.itinerary.map((day, idx) => {
                const acts = day.activities;
                const actsList = Array.isArray(acts) ? acts : (typeof acts === 'string' ? acts.split(",").map(a => a.trim()).filter(Boolean) : []);
                return (
                  <div key={idx} className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <span className="w-10 h-10 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">{day.day}</span>
                    <div className="flex-1 min-w-0">
                      <h5 className="text-base font-bold text-gray-800">{day.title || `Day ${day.day}`}</h5>
                      {day.description && <p className="text-sm text-gray-500 mt-1 leading-relaxed">{day.description}</p>}
                      {actsList.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {actsList.map((a, ai) => {
                            const label = typeof a === 'string' ? a : (a.title || a.tour_name || 'Activity');
                            return <Badge key={ai} className="bg-blue-50 text-blue-600 border-blue-100">{label}</Badge>;
                          })}
                        </div>
                      )}
                      {day.hotel && (
                        <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Hotel className="w-3 h-3" /> {day.hotel}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent></Card>
        )}

        {/* Inclusions / Exclusions */}
        {(pkg.inclusions?.length > 0 || pkg.exclusions?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pkg.inclusions?.length > 0 && (
              <Card className="bg-green-50/50 border-green-100"><CardContent className="pt-6">
                <h4 className="text-sm font-semibold text-green-700 uppercase tracking-wider mb-3">Inclusions</h4>
                {pkg.inclusions.map((inc, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-700 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /><span>{inc}</span>
                  </div>
                ))}
              </CardContent></Card>
            )}
            {pkg.exclusions?.length > 0 && (
              <Card className="bg-red-50/50 border-red-100"><CardContent className="pt-6">
                <h4 className="text-sm font-semibold text-red-700 uppercase tracking-wider mb-3">Exclusions</h4>
                {pkg.exclusions.map((exc, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-700 mb-2">
                    <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" /><span>{exc}</span>
                  </div>
                ))}
              </CardContent></Card>
            )}
          </div>
        )}

        {/* Terms & Conditions */}
        {pkg.terms_conditions && (
          <Card className="bg-indigo-50/50 border-indigo-100"><CardContent className="pt-6">
            <h4 className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FileText className="w-4 h-4" /> Terms &amp; Conditions
            </h4>
            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">{pkg.terms_conditions}</pre>
          </CardContent></Card>
        )}

        {/* Bottom CTA */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={() => navigate('/agent/packages')} className="border-gray-300">Back to Packages</Button>
          <Button onClick={() => setPdfDialog(true)} variant="outline" disabled={false} className="gap-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="pkg-detail-download-pdf-bottom">
            <Download className="w-4 h-4" /> Download PDF
          </Button>
          <Button onClick={() => setBookingDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="pkg-detail-book-now-bottom">
            <Ticket className="w-4 h-4" /> Book Now
          </Button>
        </div>
      </div>

      {/* Download PDF Configuration Dialog */}
      <Dialog open={pdfDialog} onOpenChange={setPdfDialog}>
        <DialogContent className="max-w-xl bg-white border-gray-200 text-gray-800" data-testid="pdf-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-emerald-600" /> Generate Package Quote PDF</DialogTitle>
          </DialogHeader>
          {(() => {
            const availPlans = getAvailableMealPlans();
            const combos = getSelectedCombos();
            const togglePdfOption = (i) => {
              const has = pdfForm.hotel_option_idxs.includes(i);
              let next = has ? pdfForm.hotel_option_idxs.filter(x => x !== i) : [...pdfForm.hotel_option_idxs, i];
              if (next.length === 0) next = [i]; // keep at least one
              setPdfForm({ ...pdfForm, hotel_option_idxs: next });
            };
            const togglePdfMealPlan = (p) => {
              const has = pdfForm.meal_plans.includes(p);
              let next = has ? pdfForm.meal_plans.filter(x => x !== p) : [...pdfForm.meal_plans, p];
              if (next.length === 0) next = [p];
              setPdfForm({ ...pdfForm, meal_plans: next });
            };
            return (
              <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
                {pkg.hotel_options && pkg.hotel_options.length > 1 && (
                  <div>
                    <Label className="text-sm">Hotel Options <span className="text-xs text-gray-400 font-normal">(tick any to include)</span></Label>
                    <div className="mt-1.5 space-y-1.5 bg-gray-50 border border-gray-200 rounded-lg p-2.5" data-testid="pdf-hotel-options">
                      {pkg.hotel_options.map((o, i) => {
                        const first = (o.stays && o.stays[0]) || o;
                        const label = `Option ${i + 1}${first?.hotel_name ? ` • ${first.hotel_name}` : ''}`;
                        const checked = pdfForm.hotel_option_idxs.includes(i);
                        return (
                          <label key={i} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
                            <input type="checkbox" checked={checked} onChange={() => togglePdfOption(i)} className="w-4 h-4 accent-emerald-600" data-testid={`pdf-hotel-option-${i}`} />
                            <span className={checked ? 'font-semibold' : ''}>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-sm">Meal Plans <span className="text-xs text-gray-400 font-normal">(tick any to include)</span></Label>
                  <div className="mt-1.5 flex flex-wrap gap-3 bg-gray-50 border border-gray-200 rounded-lg p-2.5" data-testid="pdf-meal-plans">
                    {availPlans.map(p => {
                      const checked = pdfForm.meal_plans.includes(p);
                      return (
                        <label key={p} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
                          <input type="checkbox" checked={checked} onChange={() => togglePdfMealPlan(p)} className="w-4 h-4 accent-emerald-600" data-testid={`pdf-meal-plan-${p}`} />
                          <span className={checked ? 'font-semibold' : ''}>{p}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Guest Name</Label>
                    <Input value={pdfForm.guest_name} onChange={e => setPdfForm({ ...pdfForm, guest_name: e.target.value })} placeholder="Client name" className="mt-1 bg-gray-50 border-gray-200" data-testid="pdf-guest-name" />
                  </div>
                  <div>
                    <Label className="text-sm">Guest Mobile</Label>
                    <Input value={pdfForm.guest_mobile} onChange={e => setPdfForm({ ...pdfForm, guest_mobile: e.target.value })} placeholder="+91 ..." className="mt-1 bg-gray-50 border-gray-200" data-testid="pdf-guest-mobile" />
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Travel Date</Label>
                  <Input type="date" value={pdfForm.travel_date} onChange={e => setPdfForm({ ...pdfForm, travel_date: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="pdf-travel-date" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">Adults</Label>
                    <Input type="number" min="1" value={pdfForm.adults} onChange={e => setPdfForm({ ...pdfForm, adults: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="pdf-adults" />
                  </div>
                  <div>
                    <Label className="text-sm">CWB</Label>
                    <Input type="number" min="0" value={pdfForm.cwb} onChange={e => setPdfForm({ ...pdfForm, cwb: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="pdf-cwb" />
                  </div>
                  <div>
                    <Label className="text-sm">CNB</Label>
                    <Input type="number" min="0" value={pdfForm.cnb} onChange={e => setPdfForm({ ...pdfForm, cnb: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="pdf-cnb" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Markup Type</Label>
                    <Select value={pdfForm.markup_type} onValueChange={v => setPdfForm({ ...pdfForm, markup_type: v })}>
                      <SelectTrigger className="mt-1 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_pax">Per Pax (INR)</SelectItem>
                        <SelectItem value="lumpsum">Lump Sum (INR)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Markup Amount (INR)</Label>
                    <Input type="number" min="0" value={pdfForm.markup_amount} onChange={e => setPdfForm({ ...pdfForm, markup_amount: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="pdf-markup" />
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={pdfForm.gst_applicable} onChange={e => setPdfForm({ ...pdfForm, gst_applicable: e.target.checked })} className="w-4 h-4 accent-emerald-600" data-testid="pdf-gst-toggle" id="pdf-gst" />
                  <label htmlFor="pdf-gst" className="text-gray-700">Add GST (5%) on Total</label>
                </div>

                {/* Live Preview — one row per selected combo */}
                <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-3 text-sm space-y-2" data-testid="pdf-preview">
                  <div className="text-xs text-gray-500">Preview of selected combinations ({combos.length})</div>
                  {combos.map((cmb, i) => {
                    const t = calcComboTotals(cmb.optIdx, cmb.mealPlan, combos.length);
                    const optObj = pkg.hotel_options?.[cmb.optIdx];
                    const optLabel = optObj ? (optObj.stays?.[0]?.hotel_name || `Option ${cmb.optIdx + 1}`) : `Option ${cmb.optIdx + 1}`;
                    return (
                      <div key={i} className="flex justify-between items-center text-gray-800 border-t border-emerald-200/60 pt-1.5 first:border-0 first:pt-0">
                        <span className="text-[12px]"><span className="font-semibold">{optLabel}</span> <span className="text-gray-500">• {cmb.mealPlan}</span></span>
                        <span className="font-mono font-bold text-emerald-700">{fmt(t.final)}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-2 pt-1">
                  <Button variant="outline" onClick={() => setPdfDialog(false)} className="flex-1 border-gray-300">Cancel</Button>
                  <Button onClick={generatePDF} disabled={generating || combos.length === 0} className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="pdf-generate-btn">
                    <Download className="w-4 h-4" /> {generating ? 'Generating...' : 'Generate PDF'}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Book Now Dialog */}
      <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
        <DialogContent className="max-w-xl bg-white border-gray-200 text-gray-800" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ticket className="w-5 h-5 text-emerald-600" /> Book Now — {pkg?.title}</DialogTitle>
          </DialogHeader>
          {(() => {
            const opt = bookingOpt();
            const availPlans = (opt?.meal_plans && opt.meal_plans.length > 0) ? opt.meal_plans : ["CPAI"];
            const totals = calcBookingTotals();
            return (
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                {pkg.hotel_options && pkg.hotel_options.length > 1 && (
                  <div>
                    <Label className="text-sm">Hotel Option</Label>
                    <Select value={String(bookingForm.hotel_option_idx)} onValueChange={v => setBookingForm({ ...bookingForm, hotel_option_idx: parseInt(v), meal_plan: "CPAI" })}>
                      <SelectTrigger className="mt-1 bg-gray-50 border-gray-200" data-testid="booking-hotel-option"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {pkg.hotel_options.map((o, i) => {
                          const first = (o.stays && o.stays[0]) || o;
                          const label = `Option ${i + 1}${first?.hotel_name ? ` • ${first.hotel_name}` : ''}`;
                          return <SelectItem key={i} value={String(i)}>{label}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-sm">Meal Plan</Label>
                  <Select value={bookingForm.meal_plan} onValueChange={v => setBookingForm({ ...bookingForm, meal_plan: v })}>
                    <SelectTrigger className="mt-1 bg-gray-50 border-gray-200" data-testid="booking-meal-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {availPlans.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Guest Name *</Label>
                    <Input value={bookingForm.guest_name} onChange={e => setBookingForm({ ...bookingForm, guest_name: e.target.value })} placeholder="Client name" className="mt-1 bg-gray-50 border-gray-200" data-testid="booking-guest-name" />
                  </div>
                  <div>
                    <Label className="text-sm">Guest Mobile</Label>
                    <Input value={bookingForm.guest_mobile} onChange={e => setBookingForm({ ...bookingForm, guest_mobile: e.target.value })} placeholder="+91 ..." className="mt-1 bg-gray-50 border-gray-200" data-testid="booking-guest-mobile" />
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Travel Date *</Label>
                  <Input type="date" value={bookingForm.travel_date} onChange={e => setBookingForm({ ...bookingForm, travel_date: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="booking-travel-date" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">Adults</Label>
                    <Input type="number" min="1" value={bookingForm.adults} onChange={e => setBookingForm({ ...bookingForm, adults: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="booking-adults" />
                  </div>
                  <div>
                    <Label className="text-sm">CWB</Label>
                    <Input type="number" min="0" value={bookingForm.cwb} onChange={e => setBookingForm({ ...bookingForm, cwb: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="booking-cwb" />
                  </div>
                  <div>
                    <Label className="text-sm">CNB</Label>
                    <Input type="number" min="0" value={bookingForm.cnb} onChange={e => setBookingForm({ ...bookingForm, cnb: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="booking-cnb" />
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={bookingForm.gst_applicable} onChange={e => setBookingForm({ ...bookingForm, gst_applicable: e.target.checked })} className="w-4 h-4 accent-emerald-600" data-testid="booking-gst-toggle" id="booking-gst" />
                  <label htmlFor="booking-gst" className="text-gray-700">Add GST (5%) on Total</label>
                </div>

                <div>
                  <Label className="text-sm">Special Requirements (optional)</Label>
                  <Input value={bookingForm.requirements} onChange={e => setBookingForm({ ...bookingForm, requirements: e.target.value })} placeholder="Dietary preferences, accessibility, etc." className="mt-1 bg-gray-50 border-gray-200" data-testid="booking-requirements" />
                </div>

                <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-3 text-sm space-y-1" data-testid="booking-summary">
                  <div className="flex justify-between text-gray-700"><span>Adults × {totals.adults} @ {fmt(totals.perAdult)}</span><span className="font-mono">{fmt(totals.perAdult * totals.adults)}</span></div>
                  {totals.cwb > 0 && <div className="flex justify-between text-gray-700"><span>CWB × {totals.cwb} @ {fmt(totals.cwbRate)}</span><span className="font-mono">{fmt(totals.cwbRate * totals.cwb)}</span></div>}
                  {totals.cnb > 0 && <div className="flex justify-between text-gray-700"><span>CNB × {totals.cnb} @ {fmt(totals.cnbRate)}</span><span className="font-mono">{fmt(totals.cnbRate * totals.cnb)}</span></div>}
                  <div className="flex justify-between text-gray-700 border-t border-emerald-200 pt-1"><span>Subtotal</span><span className="font-mono font-semibold">{fmt(totals.subtotal)}</span></div>
                  {totals.gst > 0 && <div className="flex justify-between text-gray-700"><span>GST (5%)</span><span className="font-mono">{fmt(totals.gst)}</span></div>}
                  <div className="flex justify-between text-emerald-700 text-base pt-1 border-t border-emerald-200"><span className="font-bold">ESTIMATED TOTAL</span><span className="font-mono font-bold">{fmt(totals.final)}</span></div>
                  {totals.vehicle && <p className="text-[11px] text-gray-500">Includes vehicle: {totals.vehicle}</p>}
                  <p className="text-[11px] text-gray-500 pt-1">Admin will confirm final quote & availability after booking request is submitted.</p>
                </div>

                <div className="flex gap-2 pt-1">
                  <Button variant="outline" onClick={() => setBookingDialog(false)} className="flex-1 border-gray-300">Cancel</Button>
                  {(() => {
                    const hasGuest = !!(bookingForm.guest_name?.trim() && bookingForm.guest_mobile?.trim());
                    return (
                      <Button
                        onClick={submitBooking}
                        disabled={bookingSubmitting || !hasGuest}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={hasGuest ? "Confirm Booking" : "Fill Guest Name and Mobile to enable"}
                        data-testid="booking-submit-btn"
                      >
                        {bookingSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Ticket className="w-4 h-4" /> {hasGuest ? 'Confirm Booking' : 'Add Guest Info First'}</>}
                      </Button>
                    );
                  })()}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AgentPackageDetail;
